import React, { useState, useRef, useCallback, useEffect } from 'react';
import { SignificationItem, TensionResponse } from '../../../../types/signification.types';
import { StepIndicator } from './StepIndicator';
import { NarrateStep } from './NarrateStep';
import {
  screenToTensionWeight,
  tensionPercentages,
  tensionColorBlend,
} from '../../../../utils/signification.math';
import { AUDIO } from '../../../../utils/audioFeedback';
import './TensionCapture.css';

interface TensionCaptureProps {
  item: SignificationItem;
  onComplete: (response: TensionResponse, audioBlob: Blob | null) => void;
  onClose: () => void;
  itemIndex: number;
  totalItems: number;
}

type Phase = 'place' | 'narrate';

const TRACK_PADDING = 32;

export const TensionCapture: React.FC<TensionCaptureProps> = ({
  item,
  onComplete,
  onClose,
  itemIndex,
  totalItems,
}) => {
  const [phase, setPhase] = useState<Phase>('place');
  const [weight, setWeight] = useState<number | null>(0.5);
  const [revisionCount, setRevisionCount] = useState(0);
  const startTimeRef = useRef(Date.now());
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // Reset start time when entering a new item
  useEffect(() => {
    startTimeRef.current = Date.now();
  }, [item.id]);

  const hasNarrative = item.narrativeAnchor.enabled;

  const getTrackBounds = useCallback(() => {
    if (!trackRef.current) return { left: 0, right: 300 };
    const rect = trackRef.current.getBoundingClientRect();
    return { left: rect.left + TRACK_PADDING, right: rect.right - TRACK_PADDING };
  }, []);

  const updateWeight = useCallback((clientX: number) => {
    const { left, right } = getTrackBounds();
    const w = screenToTensionWeight(clientX, left, right);
    setWeight(w);
  }, [getTrackBounds]);

  const handlePointerDown = (e: React.PointerEvent) => {
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    updateWeight(e.clientX);
    AUDIO.tap();
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging.current) return;
    updateWeight(e.clientX);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
  };

  const handleConfirm = () => {
    if (hasNarrative) {
      setPhase('narrate');
    } else {
      finalize(null);
    }
  };

  const handleAdjust = () => {
    setRevisionCount(r => r + 1);
    setPhase('place');
  };

  const handleNarrate = (audioBlob: Blob | null) => {
    finalize(audioBlob);
  };

  const finalize = (audioBlob: Blob | null) => {
    if (weight === null) return;
    const response: TensionResponse = {
      mode: 'tension',
      itemId: item.id,
      weight,
      placementDurationMs: Date.now() - startTimeRef.current,
      revisionCount,
    };
    onComplete(response, audioBlob);
  };

  // Compute gradient and percentages
  const leftColor = item.poles[0]?.color || '#C2AC8E';
  const rightColor = item.poles[1]?.color || '#8B9EB7';
  const pcts = weight !== null ? tensionPercentages(weight) : null;

  // Blend color for handle
  const handleColor = weight !== null
    ? tensionColorBlend(weight, leftColor, rightColor)
    : '#B0A89E';

  // Handle position as percentage
  const handlePct = weight !== null ? weight * 100 : 50;

  // Dynamic gradient — brightens toward the side the handle is moved to
  const leftAlpha = weight !== null
    ? Math.round((1 - weight) * 180 + 44).toString(16).padStart(2, '0')
    : '20';
  const rightAlpha = weight !== null
    ? Math.round(weight * 180 + 44).toString(16).padStart(2, '0')
    : '20';

  if (phase === 'narrate') {
    return (
      <NarrateStep
        prompt={item.narrativeAnchor.prompt}
        required={item.narrativeAnchor.required}
        onSubmit={handleNarrate}
        onBack={handleAdjust}
      />
    );
  }

  // Place phase
  return (
    <div className="tc-sig-container">
      <StepIndicator current="place" hasNarrative={hasNarrative} />

      <div className="tc-sig-header">
        {totalItems > 1 && (
          <div className="tc-sig-counter">{itemIndex + 1} of {totalItems}</div>
        )}
        <div className="tc-sig-prompt">{item.prompt}</div>
      </div>

      <div className="tc-sig-track-area">
        {/* Pole labels */}
        <div className="tc-sig-poles">
          <div className="tc-sig-pole tc-sig-pole--left">
            <span className="tc-sig-pole-label" style={{ color: leftColor }}>{item.poles[0].label}</span>
            {item.poles[0].description && (
              <span className="tc-sig-pole-desc">{item.poles[0].description}</span>
            )}
            <span className="tc-sig-pole-pct">{pcts ? `${pcts[0]}%` : '\u00A0'}</span>
          </div>
          <div className="tc-sig-pole tc-sig-pole--right">
            <span className="tc-sig-pole-label" style={{ color: rightColor }}>{item.poles[1].label}</span>
            {item.poles[1].description && (
              <span className="tc-sig-pole-desc">{item.poles[1].description}</span>
            )}
            <span className="tc-sig-pole-pct">{pcts ? `${pcts[1]}%` : '\u00A0'}</span>
          </div>
        </div>

        {/* Track */}
        <div
          ref={trackRef}
          className="tc-sig-track"
          style={{ background: `linear-gradient(to right, ${leftColor}${leftAlpha}, ${rightColor}${rightAlpha})` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          {/* Handle */}
          <div
            className="tc-sig-handle tc-sig-handle--placed"
            style={{
              left: `calc(${TRACK_PADDING}px + (100% - ${TRACK_PADDING * 2}px) * ${weight ?? 0.5})`,
              background: handleColor,
            }}
          />
        </div>

        <div className="tc-sig-hint">
          Drag to place yourself on the spectrum
        </div>
      </div>

      <div className="tc-sig-actions">
        <button
          className="tc-sig-next-btn"
          disabled={weight === null}
          onClick={() => { AUDIO.tap(); handleConfirm(); }}
        >
          Next
        </button>
      </div>
    </div>
  );
};
